import { lazy, Suspense, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useChatStore } from './stores/chatStore';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { Loader2 } from 'lucide-react';

// Chat widget - loaded at app level so it persists across page navigations
const ChatWidget = lazy(() => import('./components/ChatWidget').then(m => ({ default: m.ChatWidget })));

// Lazy load pages
const LandingPage = lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })));
// WIP - Quest pages disabled, uncomment when ready
// const LoginPage = lazy(() => import('./pages/quests/LoginPage').then(m => ({ default: m.LoginPage })));
// const QuestsLayout = lazy(() => import('./pages/quests/QuestsLayout').then(m => ({ default: m.QuestsLayout })));
// const QuestsDashboardPage = lazy(() => import('./pages/quests/DashboardPage').then(m => ({ default: m.DashboardPage })));
// const QuestsPage = lazy(() => import('./pages/quests/QuestsPage').then(m => ({ default: m.QuestsPage })));
// const LeaderboardPage = lazy(() => import('./pages/quests/LeaderboardPage').then(m => ({ default: m.LeaderboardPage })));
const VideoCallPage = lazy(() => import('./pages/VideoCallPage').then(m => ({ default: m.VideoCallPage })));
const AuthVerifyPage = lazy(() => import('./pages/AuthVerifyPage').then(m => ({ default: m.AuthVerifyPage })));
const MePage = lazy(() => import('./pages/Me'));
const QuickVipPage = lazy(() => import('./pages/QuickVipPage'));

// Monitor (blockchain monitor) pages
const MonitorLayout = lazy(() => import('./features/monitor/MonitorLayout'));
const MonitorOverviewPage = lazy(() => import('./features/monitor/pages/OverviewPage'));
const MonitorRawDataPage = lazy(() => import('./features/monitor/pages/RawDataPage'));

// Static pages
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const BlogPage = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogPage })));
const CareersPage = lazy(() => import('./pages/CareersPage').then(m => ({ default: m.CareersPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const CookiesPage = lazy(() => import('./pages/CookiesPage').then(m => ({ default: m.CookiesPage })));
const SocialCallbackPage = lazy(() => import('./pages/SocialCallbackPage'));

function LoadingFallback() {
  return (
    <div className="min-h-screen bg-near-black flex items-center justify-center relative">
      {/* Background as separate fixed layer for GPU compositing */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: 'url(https://kanji.fra1.cdn.digitaloceanspaces.com/grid-background.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          willChange: 'transform',
        }}
      />
      <Loader2 className="w-8 h-8 text-gold animate-spin relative z-10" />
    </div>
  );
}

function ProtectedRoute({ children, redirectTo = "/quests/login" }: { children: React.ReactNode, redirectTo?: string }) {
  const { token } = useAuthStore();

  if (!token) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

// Wrapper to pass invite code from path to LandingPage
function InviteLandingPage() {
  const { code } = useParams<{ code: string }>();
  return <LandingPage inviteCode={code} />;
}

function GoogleAnalyticsTracker({ children }: { children: React.ReactNode }) {
  useGoogleAnalytics();
  return <>{children}</>;
}

// Pages where chat widget should NOT appear
const CHAT_EXCLUDED_PATHS = ['/call', '/quests/login', '/auth/verify', '/qv'];

function GlobalChatWidget() {
  const location = useLocation();
  const { isOpen, initialMessage, setOpen, clearInitialMessage } = useChatStore();

  // Don't show chat on excluded pages
  const shouldShowChat = !CHAT_EXCLUDED_PATHS.some(path => location.pathname.startsWith(path));

  const handleOpenChange = useCallback((open: boolean) => {
    setOpen(open);
  }, [setOpen]);

  const handleInitialMessageSent = useCallback(() => {
    clearInitialMessage();
  }, [clearInitialMessage]);

  if (!shouldShowChat) return null;

  return (
    <Suspense fallback={null}>
      <ChatWidget
        open={isOpen}
        onOpenChange={handleOpenChange}
        initialMessage={initialMessage || undefined}
        onInitialMessageSent={handleInitialMessageSent}
      />
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <GoogleAnalyticsTracker>
      {/* Global chat widget - persists across page navigations */}
      <GlobalChatWidget />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Homepage with claim flow */}
          <Route path="/" element={<LandingPage />} />

          {/* VIP invite shortlink: /invite/CODE renders landing with invite code */}
          <Route path="/invite/:code" element={<InviteLandingPage />} />

          {/* Static pages */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/cookies" element={<CookiesPage />} />

          {/* Quick VIP code generator (secret URL) */}
          <Route path="/qv/:secret" element={<QuickVipPage />} />

          {/* Social Auth Callback */}
          <Route path="/x-callback" element={<SocialCallbackPage />} />

          {/* Quest system routes (WIP - disabled, redirecting to home) */}
          <Route path="/quests/login" element={<Navigate to="/" replace />} />
          <Route path="/quests/*" element={<Navigate to="/" replace />} />
          <Route path="/auth/verify" element={<AuthVerifyPage />} />
          <Route
            path="/me"
            element={
              <ProtectedRoute redirectTo="/">
                <MePage />
              </ProtectedRoute>
            }
          />

          {/* Video call route */}
          <Route path="/call" element={<VideoCallPage />} />

          {/* Blockchain Monitor (public, no auth required) */}
          <Route path="/monitor" element={<MonitorLayout />}>
            <Route index element={<MonitorOverviewPage />} />
            <Route path="raw-data" element={<MonitorRawDataPage />} />
          </Route>

          {/* Static pages served by Vite middleware - prevent catch-all redirect */}
          <Route path="/proposals/*" element={null} />
          <Route path="/decks/*" element={null} />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      </GoogleAnalyticsTracker>
    </BrowserRouter>
  );
}

export default App;
